import {Inject, Injectable} from "@angular/core";
import {BehaviorSubject} from "rxjs";
import {RegistrationDTO} from "./registrationdto";
import {User} from "../domain/user";
import {Address} from "../domain/address";
import {I18nServices} from "../i18n/i18n.service";
import {IS_BROWSER} from "../utils/injectiontokens";
import {LocalStorageService} from "../storage/local-storage.service";

@Injectable({
  providedIn: 'root'
})
export class RegistrationService {

  public data: RegistrationDTO = null;
  protected acceptTOUAndPP: boolean = false;
  protected volatileData: object;

  public registrationSubject: BehaviorSubject<RegistrationDTO> = new BehaviorSubject(this.data);

  constructor(@Inject(IS_BROWSER) protected isBrowser: boolean,
              protected i18nService: I18nServices,
              protected storageService: LocalStorageService) {

    this.data = new RegistrationDTO();
    this.volatileData = {};

    if(this.isBrowser) {
      const data = this.storageService.getObject<RegistrationDTO>("RegistrationData");
      if(data != null) {
        this.data = new RegistrationDTO(data);
        this.registrationSubject.next(this.data);
      }
    }

  }

  public transformUserToRegistrationData(user: User) {
    this.setEmail(user.email);
    this.setPhone(user.phone);
    this.setPhonePrefix(user.phonePrefix);
    if(!!user.address) {
      this.setFirstName(user.address.firstName);
      this.setLastName(user.address.lastName);
      this.setCountry(user.address.countryIsoCode);
      this.setCity(user.address.city);
      this.setstreet(user.address.street);
      this.setPostalCode(user.address.postalCode);
    }
    this.setAcceptTOUAndPP(true);
  }

  public saveData() {
    if(this.isBrowser) {
      this.storageService.setObject('RegistrationData', this.data);
    }
    this.registrationSubject.next(this.data);
  }

  public clearRegistration() {
    this.storageService.removeItem('RegistrationData');
    this.data = new RegistrationDTO();
  }

  public getEmail(): string {
    return this.data.email;
  }

  public setEmail(email) {
    this.data.email = email;
    this.saveData();
  }

  public getPhone(): string {
    return this.data.phone;
  }

  public setPhone(phone) {
    this.data.phone = phone;
    this.saveData();
  }

  public getPhonePrefix(): string {
    return this.data.phonePrefix;
  }

  public setPhonePrefix(phonePrefix) {
    this.data.phonePrefix = phonePrefix;
    this.saveData();
  }

  public getAcceptTOUAndPP(): boolean {
    return this.acceptTOUAndPP;
  }

  public setAcceptTOUAndPP(acceptTOUAndPP) {
    this.acceptTOUAndPP = acceptTOUAndPP;
  }

  public getFirstName(): string {
    return this.data.firstName;
  }

  public setFirstName(firstName) {
    this.data.firstName = firstName;
    this.saveData();
  }

  public getLastName(): string {
    return this.data.lastName;
  }

  public setLastName(lastName) {
    this.data.lastName = lastName;
    this.saveData();
  }

  public getFullName(): string {
    let returnValue = "";
    if(this.getFirstName() != null) {
      returnValue += this.getFirstName();
    }
    if(this.getLastName() != null) {
      if(returnValue.length > 0) {
        returnValue += " ";
      }
      returnValue += this.getLastName();
    }
    return returnValue.trim();
  }

  public getstreet(): string {
    return this.data.street;
  }

  public setstreet(street) {
    this.data.street = street;
    this.saveData();
  }

  public getApartment(): string {
    return this.data.apartment;
  }

  public setApartment(apartment) {
    this.data.apartment = apartment;
    this.saveData();
  }

  public getCity(): string {
    return this.data.city;
  }

  public setCity(city) {
    this.data.city = city;
    this.saveData();
  }

  public getRegion(): string {
    return this.data.region;
  }

  public setRegion(region) {
    this.data.region = region;
    this.saveData();
  }

  public getPostalCode(): string {
    return this.data.postalCode;
  }

  public setPostalCode(postalCode) {
    this.data.postalCode = postalCode;
    this.saveData();
  }

  public getCountry(): string {
    return this.data.countryIsoCode;
  }

  public setCountry(countryIsoCode) {
    this.data.countryIsoCode = countryIsoCode;
    this.saveData();
  }

  public getUserFromRegistrationData() {
    let user = new User();
    user.email = this.getEmail();
    user.phone = this.getPhone();
    user.phonePrefix = this.getPhonePrefix();
    user.address = new Address();
    user.language = this.i18nService.getLanguageIsoCode();
    user.address.firstName = this.getFirstName();
    user.address.lastName = this.getLastName();
    user.address.street = this.getstreet();
    user.address.apartment = this.getApartment();
    user.address.city = this.getCity();
    user.address.postalCode = this.getPostalCode();
    user.address.countryIsoCode = this.getCountry();
    return user;
  }

}
